import React, { useState, useEffect } from 'react';
import { PAIEMENT_ACTIF } from '../config/features';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '../i18n/router';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Avatar,
} from '@mui/material';
import { useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { recupererListeVoyageurs, checkUserSociete } from '../Services';
import AssuranceVisaComponent from '../components/AssuranceVisaComponent';
import { Voyageurs } from '../models/Voyageurs';
import { API_BASE_URL } from '../config/api';
interface FormScreenRouteParams {
  title: string;
  prixconsulat: number;
  prixintervention: number;
  demandeId: number;
  url_flag: string;
  validite: string;
  informations: string;
  commentaires?: string;
  titleDisplay?: string;
}

const FormKenya: React.FC = () => {
  const { t } = useTranslation('formulaires');
  const { t: tp } = useTranslation('formulaires_pays');
  // Récupération des paramètres passés via react-router (state)
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromVoyageurCreation = searchParams.get('fromCreation') === 'true';

  // Essayer de récupérer le state depuis location.state ou sessionStorage
  const getInitialState = (): FormScreenRouteParams => {
    // Si on revient de la création de voyageur, utiliser sessionStorage
    if (fromVoyageurCreation) {
      const saved = sessionStorage.getItem('formState');
      if (saved) {
        return JSON.parse(saved) as FormScreenRouteParams;
      }
    }
    // Sinon utiliser location.state et nettoyer sessionStorage
    if (location.state && Object.keys(location.state).length > 0) {
      sessionStorage.removeItem('formState');
      return location.state as FormScreenRouteParams;
    }
    return {} as FormScreenRouteParams;
  };
  const state = getInitialState();
  const titleDisplay = (state as any)?.titleDisplay || (state as any)?.title || '';
  const { title, prixconsulat, prixintervention, demandeId, url_flag, validite, informations } = state;
  const commentaires = state?.commentaires || '';
  const isEditMode = demandeId != null;

  // États pour les champs communs
  const [dateDepart, setDateDepart] = useState('');
  const [dateSortie, setDateSortie] = useState('');
  const [voyageurs, setVoyageurs] = useState<Voyageurs[]>([]);
  const [selectedVoyageur, setSelectedVoyageur] = useState('');
  const [champsNonRemplis, setChampsNonRemplis] = useState<string[]>([]);
  const [assuranceVisa, setAssuranceVisa] = useState('');
  const [sliderValue, setSliderValue] = useState(false);

  // États pour les fichiers (documents obligatoires)
  const [billetAvionFile, setBilletAvionFile] = useState<File | null>(null);
  const [billetAvionPreview, setBilletAvionPreview] = useState<string | null>(null);

  const [reservationHotelFile, setReservationHotelFile] = useState<File | null>(null);
  const [reservationHotelPreview, setReservationHotelPreview] = useState<string | null>(null);

  // Pour un visa affaire : lettre d’invitation, certificat d’enregistrement et carte d’identité accueil
  const [lettreInvitationFile, setLettreInvitationFile] = useState<File | null>(null);
  const [lettreInvitationPreview, setLettreInvitationPreview] = useState<string | null>(null);

  const [certificatEnregistrementFile, setCertificatEnregistrementFile] = useState<File | null>(null);
  const [certificatEnregistrementPreview, setCertificatEnregistrementPreview] = useState<string | null>(null);

  const [carteIdentiteAccueilFile, setCarteIdentiteAccueilFile] = useState<File | null>(null);
  const [carteIdentiteAccueilPreview, setCarteIdentiteAccueilPreview] = useState<string | null>(null);

  // Champs spécifiques pour le Kenya
  const [visitedKenya, setVisitedKenya] = useState('');
  const [eVisaNumber, setEVisaNumber] = useState('');
  const [lastVisitDate, setLastVisitDate] = useState('');
  const [lastVisitDuration, setLastVisitDuration] = useState('1 semaine');

  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hideFees] = useState<boolean>(false);

  // États pour la gestion du type d'utilisateur et l'écran de succès
  const [isClientSociete, setIsClientSociete] = useState<boolean | null>(null);
  const [showSuccessScreen, setShowSuccessScreen] = useState<boolean>(false);
  const [createdDemandeId, setCreatedDemandeId] = useState<number | null>(null);

  // Calcul du total (frais visa + service + assurance éventuelle)
  const total = prixconsulat + prixintervention + (assuranceVisa.toLowerCase() === 'oui' ? 20 : 0);

  // Fonction de formatage d'une date au format jj/mm/aaaa
  const formatDate = (input: string) => {
    const numbers = input.replace(/[^\d]/g, '');
    let formatted = '';
    for (let i = 0; i < numbers.length && i < 8; i++) {
      if (i === 2 || i === 4) {
        formatted += '/';
      }
      formatted += numbers[i];
    }
    return formatted;
  };

  // Validation du formulaire
  const validateForm = () => {
    const parseDate = (dateStr: string): Date | null => {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
      }
      return null;
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const departDate = parseDate(dateDepart);
    const sortieDate = parseDate(dateSortie);

    const errors: string[] = [];

    // Vérifications sur les dates
    if (departDate) {
      if (isNaN(departDate.getTime()) || departDate <= today) {
        errors.push(tp('kenya.errors.departureDateInvalid'));
      }
    } else {
      errors.push(tp('kenya.errors.departureDateRequired'));
    }
    if (dateSortie && sortieDate) {
      if (isNaN(sortieDate.getTime()) || (departDate && sortieDate <= departDate)) {
        errors.push(tp('kenya.errors.returnDateInvalid'));
      }
    } else if (dateSortie && !sortieDate) {
      errors.push(tp('kenya.errors.returnDateRequired'));
    }

    // Champs obligatoires
    if (!selectedVoyageur) errors.push(t('common.validation.travelerNotSelected'));
    if (!dateDepart.trim()) errors.push(t('common.validation.departureDate'));
    if (!dateSortie.trim()) errors.push(t('common.validation.returnDate'));
    if (!billetAvionFile) errors.push(tp('kenya.validation.billetAvion'));
    if (!reservationHotelFile) errors.push(tp('kenya.validation.reservationHotel'));

    // Pour un visa affaire, champs et fichier complémentaire requis
    if (title.toLowerCase().includes('affaire')) {
      if (!lettreInvitationFile) errors.push(tp('kenya.validation.lettreInvitation'));
      if (!certificatEnregistrementFile) errors.push(tp('kenya.validation.certificatEnregistrement'));
      if (!carteIdentiteAccueilFile) errors.push(tp('kenya.validation.carteIdentiteAccueil'));
    }

    // Champs liés à la visite du Kenya
    if (!visitedKenya) errors.push(tp('kenya.validation.visitedKenya'));
    if (visitedKenya.toLowerCase() === 'oui') {
      if (!eVisaNumber.trim()) errors.push(tp('kenya.validation.eVisaNumber'));
      if (!lastVisitDate.trim()) errors.push(tp('kenya.validation.lastVisitDate'));
      if (!lastVisitDuration) errors.push(tp('kenya.validation.lastVisitDuration'));
    }

    // Assurance Visa
    if (!assuranceVisa.trim()) errors.push(t('common.validation.insurance'));
    if (!sliderValue) errors.push(t('common.validation.cguAcceptance'));

    setChampsNonRemplis(errors);
    setIsFormValid(errors.length === 0);
  };

  // Revalidation à chaque modification de champs
  useEffect(() => {
    validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateDepart,
    dateSortie,
    selectedVoyageur,
    billetAvionFile,
    reservationHotelFile,
    lettreInvitationFile,
    certificatEnregistrementFile,
    carteIdentiteAccueilFile,
    visitedKenya,
    eVisaNumber,
    lastVisitDate,
    lastVisitDuration,
    assuranceVisa,
    sliderValue,
  ]);

  // Récupération de la liste des voyageurs depuis le localStorage
  useEffect(() => {
    const fetchVoyageurs = async () => {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const user = JSON.parse(userJson);
        const listeVoyageurs = await recupererListeVoyageurs(user.id_utilisateur);
        setVoyageurs(listeVoyageurs);
        if (listeVoyageurs.length === 1) {
          setSelectedVoyageur(listeVoyageurs[0].id.toString());
        } else {
          setSelectedVoyageur('');
        }
      }
    };
    fetchVoyageurs();
  }, [demandeId]);

  // Vérification si l'utilisateur appartient à une société
  useEffect(() => {
    const checkUserStatus = async () => {
      try {
        const userJson = localStorage.getItem('user');
        if (userJson) {
          const user = JSON.parse(userJson);
          const societeResult = await checkUserSociete(user.id_utilisateur);
          setIsClientSociete(societeResult.isSociete);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du statut utilisateur:', error);
        setIsClientSociete(true); // Par défaut, considérer comme société
      }
    };
    checkUserStatus();
  }, []);

  const selectedVoyageurObj = voyageurs.find(v => v.id.toString() === selectedVoyageur);

  // Gestion de l’upload des fichiers
  const handleBilletUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      setBilletAvionFile(file);
      setBilletAvionPreview(URL.createObjectURL(file));
    }
  };

  const handleReservationUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      setReservationHotelFile(file);
      setReservationHotelPreview(URL.createObjectURL(file));
    }
  };

  const handleLettreInvitationUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      setLettreInvitationFile(file);
      setLettreInvitationPreview(URL.createObjectURL(file));
    }
  };

  const handleCertificatUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      setCertificatEnregistrementFile(file);
      setCertificatEnregistrementPreview(URL.createObjectURL(file));
    }
  };

  const handleCarteIdentiteUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      setCarteIdentiteAccueilFile(file);
      setCarteIdentiteAccueilPreview(URL.createObjectURL(file));
    }
  };

  // Soumission du formulaire
  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const userJson = localStorage.getItem('user');
      let userId: string | null = null;
      if (userJson) {
        const user = JSON.parse(userJson);
        userId = user.id_utilisateur;
      }

      const etatFormulaire = champsNonRemplis.length === 0;
      const formData = new FormData();
      formData.append('etatFormulaire', String(etatFormulaire));
      formData.append('idUtilisateur', userId || '');
      formData.append('idVoyageur', selectedVoyageur);
      formData.append('titreVisa', title);
      formData.append('dateDepart', dateDepart);
      formData.append('dateSortie', dateSortie);
      formData.append('prixconsulat', String(prixconsulat));
      formData.append('prixintervention', String(prixintervention));
      formData.append('validite', validite);
      formData.append('assuranceVisa', assuranceVisa);

      // Ajout des fichiers
      if (billetAvionFile) formData.append('billetAvion', billetAvionFile);
      if (reservationHotelFile) formData.append('reservationHotel', reservationHotelFile);
      if (title.toLowerCase().includes('affaire')) {
        if (lettreInvitationFile) formData.append('lettreInvitation', lettreInvitationFile);
        if (certificatEnregistrementFile) formData.append('certificatEnregistrement', certificatEnregistrementFile);
        if (carteIdentiteAccueilFile) formData.append('carteIdentiteAccueil', carteIdentiteAccueilFile);
      }

      // Données concernant la visite du Kenya
      formData.append('visitedKenya', visitedKenya);
      if (visitedKenya.toLowerCase() === 'oui') {
        formData.append('eVisaNumber', eVisaNumber);
        formData.append('lastVisitDate', lastVisitDate);
        formData.append('lastVisitDuration', lastVisitDuration);
      }

      // Envoi vers le backend (URL à adapter si besoin)
      const response = await axios.post(`${API_BASE_URL}/validerDemande`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setIsLoading(false);

      // Stocker l'ID de la demande créée si disponible
      if (response && response.data && response.data.idDemande) {
        setCreatedDemandeId(response.data.idDemande);
      }

      // Afficher l'écran de succès au lieu de naviguer directement
      setShowSuccessScreen(true);
    } catch (error) {
      console.error("Erreur lors de la validation de la demande :", error);
      setIsLoading(false);
    }
  };

  // Navigation vers le paiement SumUp (pour particuliers hors société)
  const handlePayerEnLigne = () => {
    if (createdDemandeId) {
      navigate('/paiement', {
        state: {
          bonId: 0,
          urlPaiement: '',
          demandeIds: [createdDemandeId],
          modePaiementSumup: true,
        },
      });
    } else {
      navigate('/retour', { state: { tabValue: 2, showPaymentMessage: true } });
    }
  };

  // Créer une nouvelle demande (reste sur le même formulaire)
  const handleNouvelleDemande = () => {
    setShowSuccessScreen(false);
    setCreatedDemandeId(null);
    setDateDepart('');
    setDateSortie('');
    setSelectedVoyageur(voyageurs.length === 1 ? voyageurs[0].id.toString() : '');
    setAssuranceVisa('');
    setSliderValue(false);
    setBilletAvionFile(null);
    setBilletAvionPreview(null);
    setReservationHotelFile(null);
    setReservationHotelPreview(null);
    setLettreInvitationFile(null);
    setLettreInvitationPreview(null);
    setCertificatEnregistrementFile(null);
    setCertificatEnregistrementPreview(null);
    setCarteIdentiteAccueilFile(null);
    setCarteIdentiteAccueilPreview(null);
    setVisitedKenya('');
    setEVisaNumber('');
    setLastVisitDate('');
    setLastVisitDuration('1 semaine');
  };

  // Fonction vide pour le onClick si désactivé
  const disabledPressHandler = () => {};

  // Si l'écran de succès est affiché
  if (showSuccessScreen) {
    return (
      <Box
        sx={{ backgroundColor: '#ECE4D3', minHeight: '100vh', py: 4 }}
      >
        <Container maxWidth="md">
          <Box sx={{ backgroundColor: '#fff', p: 4, borderRadius: 2, boxShadow: 2, textAlign: 'center' }}>
            {/* En-tête avec drapeau */}
            {url_flag && (
              <Box
                component="img"
                src={url_flag}
                alt={t('common.alt.flag')}
                sx={{ width: 60, height: 36, mb: 2, mx: 'auto' }}
              />
            )}

            {/* Message de succès */}
            <Typography variant="h4" sx={{ color: 'green', fontWeight: 'bold', mb: 2 }}>
              {t('success.title')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t('success.message', { title: titleDisplay })}
            </Typography>

            {/* Boutons conditionnels selon le type d'utilisateur */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', mt: 4 }}>
              {/* Bouton Payer en ligne - uniquement pour particuliers (hors société) */}
              {isClientSociete === false && !hideFees && (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handlePayerEnLigne}
                  sx={{ minWidth: 280 }}
                >
                  {PAIEMENT_ACTIF ? t('success.payOnline') : t('success.launchDemande')}
                </Button>
              )}

              {/* Bouton Créer une nouvelle demande - pour tous les utilisateurs */}
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={handleNouvelleDemande}
                sx={{ minWidth: 280 }}
              >
                {t('success.newRequest')}
              </Button>

              {/* Bouton Voir mes demandes */}
              <Button
                variant="text"
                color="inherit"
                onClick={() => navigate('/retour', { state: { tabValue: 2 } })}
                sx={{ mt: 1 }}
              >
                {t('success.viewRequests')}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box component="form" noValidate autoComplete="off" sx={{ backgroundColor: '#ECE4D3', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="md">
        <Box sx={{ backgroundColor: '#fff', p: 3, borderRadius: 2, boxShadow: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            {url_flag && (
              <Box
                component="img"
                src={url_flag}
                alt={t('common.alt.flag')}
                sx={{ width: 50, height: 30, mb: 1, mx: 'auto' }}
              />
            )}
            <Typography variant="h5" component="h1" gutterBottom>
              {t('common.formTitle', { title: titleDisplay })}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {validite}
              <br />
              {informations}
            </Typography>
            <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mt: 1 }}>
              {t('common.back')}
            </Button>
          </Box>

          {/* Affichage des commentaires du visa en HTML */}
          {commentaires && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                backgroundColor: '#fff3cd',
                borderRadius: 1,
                border: '1px solid #ffc107',
              }}
              dangerouslySetInnerHTML={{ __html: commentaires }}
            />
          )}

          {/* Sélection du voyageur (si non en mode édition) */}
          {!isEditMode && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="voyageur-select-label">{t('common.selectTraveler')}</InputLabel>
                <Select
                  labelId="voyageur-select-label"
                  id="voyageur-select"
                  value={selectedVoyageur}
                  label={t('common.selectTraveler')}
                  onChange={(e) => setSelectedVoyageur(e.target.value)}
                >
                  {voyageurs.map(v => (
                    <MenuItem key={v.id} value={v.id.toString()}>
                      {v.nom} {v.prenom}
                      {v.numeropax ? t('common.selectTravelerWithPassport', { passport: v.numeropax }) : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Bouton pour créer un nouveau voyageur */}
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    sessionStorage.setItem('formState', JSON.stringify({
                      title,
                      prixconsulat,
                      prixintervention,
                      url_flag,
                      validite,
                      informations,
                      commentaires
                    }));
                    navigate(`/formulaire_creation_voyageur?returnUrl=${location.pathname}`);
                  }}
                  sx={{
                    color: '#D89D2F',
                    borderColor: '#D89D2F',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      borderColor: '#B5811F'
                    }
                  }}
                >
                  {t('common.createNewTraveler')}
                </Button>
              </Box>
            </>
          )}

          {/* Avatar du voyageur sélectionné */}
          {selectedVoyageurObj && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Avatar
                src={selectedVoyageurObj.url || 'https://example.com/default-avatar.png'}
                alt={t('common.alt.avatar')}
                sx={{ width: 100, height: 100 }}
              />
            </Box>
          )}

          <TextField
            label={t('common.departureDate.shortLabel')}
            placeholder={t('common.departureDate.placeholder')}
            value={dateDepart}
            onChange={(e) => setDateDepart(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />
          <TextField
            label={t('common.returnDate.shortLabel')}
            placeholder={t('common.returnDate.placeholder')}
            value={dateSortie}
            onChange={(e) => setDateSortie(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />

          {/* Fichiers – Billet d'avion et Réservation d'hôtel */}
          <Box sx={{ my: 2 }}>
            <Typography variant="body1" gutterBottom>
              {tp('kenya.fields.billetAvion')}
            </Typography>
            <input type="file" accept="image/*,application/pdf" onChange={handleBilletUpload} />
            {billetAvionPreview && (
              <Box sx={{ mt: 2 }}>
                <img src={billetAvionPreview} alt={tp('kenya.alt.billetPreview')} style={{ maxWidth: '100%', maxHeight: 300 }} />
              </Box>
            )}
          </Box>

          <Box sx={{ my: 2 }}>
            <Typography variant="body1" gutterBottom>
              {tp('kenya.fields.reservationHotel')}
            </Typography>
            <input type="file" accept="image/*,application/pdf" onChange={handleReservationUpload} />
            {reservationHotelPreview && (
              <Box sx={{ mt: 2 }}>
                <img src={reservationHotelPreview} alt={tp('kenya.alt.reservationPreview')} style={{ maxWidth: '100%', maxHeight: 300 }} />
              </Box>
            )}
          </Box>

          {/* Pour visa affaire, documents supplémentaires */}
          {title.toLowerCase().includes('affaire') && (
            <>
              <Box sx={{ my: 2 }}>
                <Typography variant="body1" gutterBottom>
                  {tp('kenya.fields.lettreInvitation')}
                </Typography>
                <input type="file" accept="image/*,application/pdf" onChange={handleLettreInvitationUpload} />
                {lettreInvitationPreview && (
                  <Box sx={{ mt: 2 }}>
                    <img src={lettreInvitationPreview} alt={tp('kenya.alt.lettrePreview')} style={{ maxWidth: '100%', maxHeight: 300 }} />
                  </Box>
                )}
              </Box>
              <Box sx={{ my: 2 }}>
                <Typography variant="body1" gutterBottom>
                  {tp('kenya.fields.certificatEnregistrement')}
                </Typography>
                <input type="file" accept="image/*,application/pdf" onChange={handleCertificatUpload} />
                {certificatEnregistrementPreview && (
                  <Box sx={{ mt: 2 }}>
                    <img src={certificatEnregistrementPreview} alt={tp('kenya.alt.certificatPreview')} style={{ maxWidth: '100%', maxHeight: 300 }} />
                  </Box>
                )}
              </Box>
              <Box sx={{ my: 2 }}>
                <Typography variant="body1" gutterBottom>
                  {tp('kenya.fields.carteIdentiteAccueil')}
                </Typography>
                <input type="file" accept="image/*,application/pdf" onChange={handleCarteIdentiteUpload} />
                {carteIdentiteAccueilPreview && (
                  <Box sx={{ mt: 2 }}>
                    <img src={carteIdentiteAccueilPreview} alt={tp('kenya.alt.cartePreview')} style={{ maxWidth: '100%', maxHeight: 300 }} />
                  </Box>
                )}
              </Box>
            </>
          )}

          {/* Champ de sélection sur la visite du Kenya */}
          <FormControl fullWidth sx={{ my: 2 }}>
            <InputLabel id="visited-kenya-label">{tp('kenya.questions.visitedKenya')}</InputLabel>
            <Select
              labelId="visited-kenya-label"
              id="visited-kenya-select"
              value={visitedKenya}
              label={tp('kenya.questions.visitedKenya')}
              onChange={(e) => setVisitedKenya(e.target.value)}
            >
              <MenuItem value="Oui">{t('common.yes')}</MenuItem>
              <MenuItem value="Non">{t('common.no')}</MenuItem>
            </Select>
          </FormControl>

          {visitedKenya.toLowerCase() === 'oui' && (
            <>
              <TextField
                label={tp('kenya.fields.eVisaNumber')}
                placeholder={tp('kenya.fields.eVisaNumber')}
                value={eVisaNumber}
                onChange={(e) => setEVisaNumber(e.target.value)}
                fullWidth
                margin="normal"
              />
              <TextField
                label={tp('kenya.fields.lastVisitDate.label')}
                placeholder={tp('kenya.fields.lastVisitDate.placeholder')}
                value={lastVisitDate}
                onChange={(e) => setLastVisitDate(formatDate(e.target.value))}
                fullWidth
                margin="normal"
              />
              <FormControl fullWidth sx={{ my: 2 }}>
                <InputLabel id="last-visit-duration-label">{tp('kenya.fields.lastVisitDuration')}</InputLabel>
                <Select
                  labelId="last-visit-duration-label"
                  id="last-visit-duration-select"
                  value={lastVisitDuration}
                  label={tp('kenya.fields.lastVisitDuration')}
                  onChange={(e) => setLastVisitDuration(e.target.value)}
                >
                  <MenuItem value="1 semaine">{tp('kenya.durationOptions.unSemaine')}</MenuItem>
                  <MenuItem value="2 semaines">{tp('kenya.durationOptions.deuxSemaines')}</MenuItem>
                  <MenuItem value="3 semaines">{tp('kenya.durationOptions.troisSemaines')}</MenuItem>
                  <MenuItem value="1 mois">{tp('kenya.durationOptions.unMois')}</MenuItem>
                  <MenuItem value="2 mois">{tp('kenya.durationOptions.deuxMois')}</MenuItem>
                  <MenuItem value="3 mois">{tp('kenya.durationOptions.troisMois')}</MenuItem>
                </Select>
              </FormControl>
            </>
          )}

          {/* Module Assurance Visa */}
          <Box sx={{ my: 2 }}>
            <AssuranceVisaComponent
              assuranceVisa={assuranceVisa}
              setAssuranceVisa={setAssuranceVisa}
              sliderValue={sliderValue}
              setSliderValue={setSliderValue}
            />
          </Box>

          {/* Récapitulatif des frais */}
          {!hideFees && (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Typography variant="body1">{t('common.fees.visa', { prix: prixconsulat })}</Typography>
              <Typography variant="body1">{t('common.fees.service', { prix: prixintervention })}</Typography>
              <Typography variant="h6" sx={{ mt: 1 }}>
                {t('common.fees.total', { total })}
              </Typography>
            </Box>
          )}

          {/* Affichage de l'état du formulaire */}
          <Box sx={{ my: 2, p: 2, borderRadius: 1, backgroundColor: champsNonRemplis.length > 0 ? '#ffcccb' : '#ccffcc' }}>
            {champsNonRemplis.length > 0 ? (
              <>
                <Typography variant="h6" align="center">{t('common.formStatus.incomplete')}</Typography>
                {champsNonRemplis.map((champ, index) => (
                  <Typography key={index}>{champ}</Typography>
                ))}
              </>
            ) : (
              <Typography align="center">{t('common.formStatus.complete')}</Typography>
            )}
          </Box>

          {/* Bouton de validation */}
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Button
                variant="contained"
                onClick={isFormValid ? handleSubmit : disabledPressHandler}
                disabled={!isFormValid}
                sx={{ backgroundColor: isFormValid ? 'black' : '#A9A9A9' }}
              >
                {t('common.submit')}
              </Button>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default FormKenya;
